import { errorDetail } from "../../../helper/errorDetail";
import { isTerminalBatchStatus } from "../../../helper/onboardingActions";
import { statusPollRetryDelay } from "../../Workspaces/batchStatusPolling";

export const ONBOARDING_POLL_INTERVAL_MS = 3000;

/** Honest, non-retrying copy for a refused submission. */
export function onboardingSubmitError(response, action) {
  const name = `“${action.label}”`;
  const rest = "The remaining actions were not started.";
  if (response?.status === 402) {
    return `There are not enough credits to run ${name}. ${rest}`;
  }
  if (response?.status === 403) {
    return `${name} isn't available on your plan. ${rest}`;
  }
  if (response?.status === 409) {
    return `Another enrichment is still running, so ${name} was not started. ${rest}`;
  }
  const detail = errorDetail(response?.data?.detail, "");
  return `We could not start ${name}${detail ? `: ${detail}` : "."} ${rest}`;
}

const lostTrackError = (action) =>
  `We could not check on “${action.label}”. Its results will appear in this saved search when it finishes; the remaining actions were not started.`;

async function settle({ batchId, getStatus, wait, isCancelled }) {
  let failures = 0;
  for (;;) {
    if (isCancelled()) return { cancelled: true };
    let response;
    try {
      response = await getStatus(batchId);
    } catch (error) {
      response = error?.response || { status: undefined };
    }
    if (response?.status === 200) {
      failures = 0;
      const status = response.data?.status;
      if (isTerminalBatchStatus(status)) return { status };
      await wait(ONBOARDING_POLL_INTERVAL_MS);
    } else {
      const delay = statusPollRetryDelay(response?.status, failures);
      if (delay === null) return { lost: true };
      failures += 1;
      await wait(delay);
    }
  }
}

/**
 * Run the wizard actions strictly one at a time.
 *
 * The backend admits one pending/running batch per user
 * (batch_enrichment.py get_active_batch -> 409), so each submission waits until
 * the previous batch is accepted and has left the active states. A refused
 * submission stops the chain: no retry loop.
 */
export async function runOnboardingActions({
  actions, accepted, submit, getStatus, wait, onProgress, onAccepted, onBatchSettled, isCancelled,
}) {
  const items = actions.map((action) => ({
    ...action,
    batchId: accepted[action.query]?.batchId || null,
    status: accepted[action.query]?.status || "queued",
  }));
  const update = (index, patch) => {
    items[index] = { ...items[index], ...patch };
    onProgress(items.map((item) => ({ ...item })));
  };
  const stop = (index, error) => {
    for (let rest = index; rest < items.length; rest += 1) {
      if (!items[rest].batchId) items[rest] = { ...items[rest], status: "not_started" };
    }
    onProgress(items.map((item) => ({ ...item })));
    return { outcome: "stopped", error };
  };

  onProgress(items.map((item) => ({ ...item })));
  for (let index = 0; index < items.length; index += 1) {
    if (isCancelled()) return { outcome: "cancelled", error: null };
    const action = actions[index];
    if (isTerminalBatchStatus(items[index].status)) continue;

    if (!items[index].batchId) {
      update(index, { status: "submitting" });
      let response;
      try {
        response = await submit(action);
      } catch (error) {
        response = error?.response;
      }
      const batchId = response?.status === 200 ? response.data?.batch_id : null;
      if (!batchId) return stop(index, onboardingSubmitError(response, action));
      onAccepted(action, batchId);
      update(index, { batchId, status: "pending" });
      if (isCancelled()) return { outcome: "cancelled", error: null };
    }

    const result = await settle({ batchId: items[index].batchId, getStatus, wait, isCancelled });
    if (result.cancelled) return { outcome: "cancelled", error: null };
    if (result.lost) return stop(index + 1, lostTrackError(action));
    update(index, { status: result.status });
    onBatchSettled(items[index].batchId);
  }
  return { outcome: "done", error: null };
}
